import type { PlanningBatch } from '../../api/types';

/**
 * Geometrie du planning proportionnel : conversion temps <-> pixels.
 *
 * L'echelle `pxPerMinute` est identique pour tous les moyens et tous les
 * ecrans ; seul l'horizon visible change selon la largeur. C'est ce qui garantit
 * qu'une fournee de 8 h est rendue deux fois plus large qu'une de 4 h.
 */
export interface TimelineScale {
  start: Date;
  end: Date;
  pxPerMinute: number;
}

const MINUTE_MS = 60_000;

export function makeScale(start: Date, end: Date, widthPx: number): TimelineScale {
  const minutes = (end.getTime() - start.getTime()) / MINUTE_MS;
  return { start, end, pxPerMinute: minutes > 0 ? widthPx / minutes : 0 };
}

export function xOf(scale: TimelineScale, date: Date | string): number {
  const t = typeof date === 'string' ? new Date(date).getTime() : date.getTime();
  return ((t - scale.start.getTime()) / MINUTE_MS) * scale.pxPerMinute;
}

export function widthBetween(scale: TimelineScale, a: Date | string, b: Date | string): number {
  return xOf(scale, b) - xOf(scale, a);
}

/** Les 4 sous-blocs d'une fournee, en pixels, prets a positionner. */
export interface BatchGeometry {
  left: number;
  width: number;
  loadWidth: number;
  cycleWidth: number;
  uncertaintyWidth: number;
  unloadWidth: number;
  hasUncertainty: boolean;
}

export function batchGeometry(scale: TimelineScale, batch: PlanningBatch): BatchGeometry {
  const left = xOf(scale, batch.t0);
  const loadWidth = widthBetween(scale, batch.t0, batch.t1);
  const cycleWidth = widthBetween(scale, batch.t1, batch.t2min);
  const uncertaintyWidth = widthBetween(scale, batch.t2min, batch.t2max);
  const unloadWidth = widthBetween(scale, batch.t2max, batch.t3);
  return {
    left,
    width: widthBetween(scale, batch.t0, batch.t3),
    loadWidth,
    cycleWidth,
    uncertaintyWidth,
    unloadWidth,
    hasUncertainty: uncertaintyWidth > 0.5,
  };
}

/** Repere vertical (debut de journee ou de creneau) sur l'axe. */
export interface TimeTick {
  x: number;
  label: string;
  major: boolean;
}

/**
 * Genere les reperes de l'axe : un trait majeur a chaque minuit (avec la date),
 * un trait mineur toutes les `minorHours` heures.
 */
export function timeTicks(scale: TimelineScale, minorHours = 6): TimeTick[] {
  const ticks: TimeTick[] = [];
  const cursor = new Date(scale.start);
  cursor.setUTCMinutes(0, 0, 0);
  // Aligner sur un multiple de minorHours.
  cursor.setUTCHours(Math.floor(cursor.getUTCHours() / minorHours) * minorHours);

  while (cursor <= scale.end) {
    const isMidnight = cursor.getUTCHours() === 0;
    ticks.push({
      x: xOf(scale, cursor),
      major: isMidnight,
      label: isMidnight
        ? cursor.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
        : `${String(cursor.getUTCHours()).padStart(2, '0')}h`,
    });
    cursor.setUTCHours(cursor.getUTCHours() + minorHours);
  }
  return ticks;
}

/** Bandes de fond jour/nuit, pour situer matin/apres-midi/nuit sans les figer. */
export interface DayBand {
  x: number;
  width: number;
  kind: 'day' | 'night';
}

export function dayBands(scale: TimelineScale): DayBand[] {
  const bands: DayBand[] = [];
  const cursor = new Date(scale.start);
  cursor.setUTCHours(0, 0, 0, 0);

  while (cursor < scale.end) {
    // Jour 06:00-22:00, nuit sinon (repere visuel, non normatif).
    const dayStart = new Date(cursor);
    dayStart.setUTCHours(6);
    const dayEnd = new Date(cursor);
    dayEnd.setUTCHours(22);
    bands.push({ x: xOf(scale, dayStart), width: widthBetween(scale, dayStart, dayEnd), kind: 'day' });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return bands;
}
